import type { CancelablePromise } from '@services/requests/core/CancelablePromise';
import { OpenAPIHousien } from '@services/requests/core/OpenAPI';
import { request as __request } from '@services/requests/core/request';

import type {
  Alert,
  CreateAlertInput,
  UpdateAlertInput,
} from '../types/alert.types';

/**
 * Typed client for the recurring-alert registry.
 *
 * Wraps the backend `/agencies/{agencyId}/alerts` routes. The create/persist/
 * read-back/toggle slice is live; scheduled firing is a separate, deferred
 * capability, so there is no "run now" or history endpoint here.
 */
export class AlertService {
  /** List the agency's alerts for the authenticated broker, newest first. */
  public static listAlerts(
    agencyId: string,
    status?: string,
  ): CancelablePromise<Alert[]> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agencies/{agencyId}/alerts',
      path: { agencyId },
      query: { ...(status ? { status } : {}) },
    });
  }

  /** Read a single alert back (the persistence read-back). */
  public static getAlert(
    agencyId: string,
    alertId: string,
  ): CancelablePromise<Alert> {
    return __request(OpenAPIHousien, {
      method: 'GET',
      url: '/agencies/{agencyId}/alerts/{alertId}',
      path: { agencyId, alertId },
    });
  }

  /** Create a new recurring alert. */
  public static createAlert(
    agencyId: string,
    body: CreateAlertInput,
  ): CancelablePromise<Alert> {
    return __request(OpenAPIHousien, {
      method: 'POST',
      url: '/agencies/{agencyId}/alerts',
      path: { agencyId },
      body,
      mediaType: 'application/json',
    });
  }

  /**
   * Update an alert's fields or toggle its active/inactive status.
   *
   * The toggle is a real persisted write (`status`), never cosmetic-only
   * state — the list re-fetches to reflect it.
   */
  public static updateAlert(
    agencyId: string,
    alertId: string,
    body: UpdateAlertInput,
  ): CancelablePromise<Alert> {
    return __request(OpenAPIHousien, {
      method: 'PUT',
      url: '/agencies/{agencyId}/alerts/{alertId}',
      path: { agencyId, alertId },
      body,
      mediaType: 'application/json',
    });
  }

  /** Delete an alert. */
  public static deleteAlert(
    agencyId: string,
    alertId: string,
  ): CancelablePromise<{ status: string }> {
    return __request(OpenAPIHousien, {
      method: 'DELETE',
      url: '/agencies/{agencyId}/alerts/{alertId}',
      path: { agencyId, alertId },
    });
  }
}
